/**
 * Agent tool input-schema generation.
 *
 * Converts a user-authored {@link ParameterSetup} into the JSON Schema that
 * becomes `tool.function.parameters.properties` and is sent verbatim to the
 * model providers (OpenAI tolerates loose schemas; Anthropic strictly validates
 * JSON Schema draft 2020-12 and rejects the ENTIRE request with a 400 if any
 * tool schema is malformed).
 *
 * This is the SINGLE SOURCE of that logic. It used to be duplicated, byte for
 * byte, in the backend (`agent-tool-configs/helpers.ts`) and the frontend
 * (`ConfigureSkills/common/utils.ts`); both copies could emit draft-2020-12
 * INVALID output (e.g. `type: "array"` with `items: undefined`, or throwing on
 * a nested object with no properties). The generator below is hardened so it
 * can never emit a structurally invalid node:
 *   - an array node ALWAYS has a valid `items` schema (never `undefined`)
 *   - an object node ALWAYS has a `properties` map (never `undefined`)
 *   - `undefined`/`null`/unknown-typed entries are skipped, never partially built
 *   - nothing throws on malformed input
 *
 * Nodes are emitted as {@link DataSchemaType} — the shared schema type already
 * used across the tools domain — so the output is directly consumable by the
 * builder UI and the provider tool-conversion layer alike.
 *
 * HTML stripping of descriptions is environment-specific (the frontend uses the
 * browser `DOMParser`, the backend a Node implementation), so it is injected via
 * `stripDescription` rather than hard-wired here — keeping this module portable
 * across the client and server entry points.
 */

import { isBlankPrompt } from "./parameter-prompt";
import { resolveParameterType } from "./parameter-type";
import { type DataSchemaType } from "./schema-type";
import {
  type ParameterItemSchema,
  type ParameterSetup,
  type ParameterValue,
  WorkflowParameterType
} from "./workflow.types";

/** The `properties` map of a tool's `input_schema` (keyed by parameter name). */
export type AgentToolInputSchema = Record<string, DataSchemaType>;

/** A single entry of a {@link ParameterSetup}. */
type ParameterSetupEntry = NonNullable<ParameterSetup[keyof ParameterSetup]>;

/** Description passthrough used when no environment-specific stripper is given. */
const identityStrip = (raw: string): string => raw;

const SCALAR_TYPES: ReadonlySet<WorkflowParameterType> = new Set([
  WorkflowParameterType.STRING,
  WorkflowParameterType.NUMBER,
  WorkflowParameterType.BOOLEAN
]);

const EMITTABLE_TYPES: ReadonlySet<WorkflowParameterType> = new Set([
  WorkflowParameterType.STRING,
  WorkflowParameterType.NUMBER,
  WorkflowParameterType.BOOLEAN,
  WorkflowParameterType.ARRAY,
  WorkflowParameterType.OBJECT
]);

/** Extract enum values from a parameter's constraints, if any non-empty set exists. */
function readEnumValues(entry: ParameterSetupEntry): ParameterValue[] | undefined {
  const constraints = entry.constraints;
  if (!constraints) return undefined;

  if (Array.isArray(constraints.allowedValues) && constraints.allowedValues.length > 0) {
    return constraints.allowedValues;
  }
  if (Array.isArray(constraints.allowedOptions) && constraints.allowedOptions.length > 0) {
    return constraints.allowedOptions.map((option) => option.value);
  }
  return undefined;
}

/** A concrete, always-valid default item node for an array of unknown shape. */
const SCHEMA_ANY_ITEM = (): DataSchemaType => ({ type: WorkflowParameterType.STRING });

/**
 * Normalize a raw JSON-schema fragment (an array's `items` or a nested
 * `properties` entry — stored loosely as `Record<string, unknown>`) into a
 * valid draft 2020-12 {@link DataSchemaType}. Recurses through nested
 * objects/arrays so array-of-object keeps its fields and array-of-array keeps
 * a valid inner `items`. Always returns a concrete, valid node and never throws.
 *
 * Exported for consumers that hold a loose JSON-schema fragment (e.g. a
 * CUSTOM_MANIFEST action field's dereferenced OpenAPI `schema`) and need the
 * shared {@link DataSchemaType} shape the schema-driven UI inputs consume.
 */
export function normalizeRawSchemaNode(raw: unknown): DataSchemaType {
  if (typeof raw !== "object" || raw === null) {
    return { type: WorkflowParameterType.STRING };
  }

  const fragment = raw as Record<string, unknown>;

  // Resolved, not compared: a stored `integer` is a NUMBER, and matching against
  // the WorkflowParameterType values alone silently degraded every
  // array-of-integer into an array of STRINGS on the wire. This is the right
  // place to normalize — nothing here is persisted, so no stored spelling
  // changes and the republish sync sees no shape change.
  const resolved = resolveParameterType(fragment.type);
  const type =
    resolved !== undefined && EMITTABLE_TYPES.has(resolved)
      ? resolved
      : WorkflowParameterType.STRING;

  const node: DataSchemaType = { type };
  if (typeof fragment.description === "string") node.description = fragment.description;
  if (Array.isArray(fragment.enum) && fragment.enum.length > 0) node.enum = fragment.enum;

  if (type === WorkflowParameterType.OBJECT) {
    // Preserve nested fields rather than collapsing to an empty object shape.
    node.properties =
      fragment.properties && typeof fragment.properties === "object"
        ? mapRawProperties(fragment.properties as Record<string, unknown>)
        : {};
  } else if (type === WorkflowParameterType.ARRAY) {
    // A nested array still needs its own `items` to be valid draft 2020-12.
    node.items = "items" in fragment ? normalizeRawSchemaNode(fragment.items) : SCHEMA_ANY_ITEM();
  }

  return node;
}

/** Map a raw properties record into valid {@link DataSchemaType} nodes. */
function mapRawProperties(raw: Record<string, unknown>): Record<string, DataSchemaType> {
  const properties: Record<string, DataSchemaType> = {};
  for (const key of Object.keys(raw)) {
    properties[key] = normalizeRawSchemaNode(raw[key]);
  }
  return properties;
}

/**
 * Build the `items` schema for an array parameter. Always returns a valid node
 * with a concrete type (and, for object/array items, valid nested
 * properties/items) so the array is never emitted with `items: undefined` or an
 * items-less nested array. Defaults to `string` when the item shape is unknown.
 */
function buildArrayItems(
  itemsSchema: ParameterItemSchema | undefined,
  enumValues: ParameterValue[] | undefined
): DataSchemaType {
  const node = itemsSchema ? normalizeRawSchemaNode(itemsSchema) : SCHEMA_ANY_ITEM();
  if (enumValues) node.enum = enumValues;
  return node;
}

/** Build a single draft-2020-12 node from a parameter setup entry, or null to skip it. */
function buildNode(
  entry: ParameterSetupEntry,
  stripDescription: (raw: string) => string
): DataSchemaType | null {
  // Resolved, like every other read of a stored type. Matching `entry.type`
  // against the canonical values alone DROPPED the parameter outright — a
  // dashboard-attached `integer` or Pipedream `string[]` never reached the model
  // at all, which is the same symptom as a blank description and harder to see.
  const type = resolveParameterType(entry.type);
  if (type === undefined || !EMITTABLE_TYPES.has(type)) return null;

  const node: DataSchemaType = { type };

  // An absent description and `description: ""` are NOT the same thing at the
  // provider: the empty string reads as an authored blank, costs tokens and
  // tells the model nothing. This used to emit one for every parameter whose
  // prompt the author left empty, which is how over half of the active skills
  // in production ended up with at least one argument the model had no guidance
  // on. Emitting the key at all is now conditional on there being words in it.
  const description = stripDescription(entry.prompt ?? "");
  if (!isBlankPrompt(description)) node.description = description.trim();

  if (SCALAR_TYPES.has(type)) {
    const enumValues = readEnumValues(entry);
    if (enumValues) node.enum = enumValues;
    return node;
  }

  if (type === WorkflowParameterType.ARRAY) {
    node.items = buildArrayItems(entry.itemsSchema, readEnumValues(entry));
    return node;
  }

  // object — always emit a valid (possibly empty) properties map.
  node.properties = entry.properties ? buildProperties(entry.properties, stripDescription) : {};
  return node;
}

/** Build the properties map for an object schema from a nested {@link ParameterSetup}. */
function buildProperties(
  setup: ParameterSetup,
  stripDescription: (raw: string) => string
): Record<string, DataSchemaType> {
  const properties: Record<string, DataSchemaType> = {};
  for (const key of Object.keys(setup)) {
    const entry = setup[key];
    if (!entry) continue;
    const node = buildNode(entry, stripDescription);
    if (node) properties[key] = node;
  }
  return properties;
}

/**
 * Compute the agent tool input-schema `properties` map from a parameter setup.
 *
 * Only parameters whose `handler` is `"prompt"` (i.e. the agent fills them) are
 * included. The output is always valid JSON Schema draft 2020-12.
 *
 * @param parametersSetup - the user-authored parameter configuration
 * @param stripDescription - environment-specific HTML/entity stripper for the
 *   parameter prompt (defaults to identity when running where no DOM/Node
 *   stripper is available)
 */
export function computeAgentInputSchema(
  parametersSetup: ParameterSetup,
  stripDescription: (raw: string) => string = identityStrip
): AgentToolInputSchema {
  const properties: AgentToolInputSchema = {};
  if (!parametersSetup) return properties;

  for (const key of Object.keys(parametersSetup)) {
    const entry = parametersSetup[key];
    if (!entry || entry.handler !== "prompt") continue;

    const node = buildNode(entry, stripDescription);
    if (node) properties[key] = node;
  }

  return properties;
}

/**
 * Stored `agentInputSchema` shape: a flat map keyed by parameter name, where each
 * value is a JSON-Schema property definition (`{ type, description, enum, ... }`).
 *
 * This is the format the agent inference path consumes — `createFunctionsFromAgentToolConfig`
 * spreads it directly into the LLM function's `parameters.properties`, and the MCP
 * `buildZodSchema` reads each field's `type`/`enum`/`required`. It is NOT a wrapped
 * JSON Schema (`{ type: "object", properties, required }`).
 */
export type FlatAgentInputSchema = Record<string, Record<string, unknown>>;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Normalize a caller-supplied (or already-stored) `agentInputSchema` into the
 * flat, property-keyed shape, preserving per-property `enum`, `description`,
 * nested `properties`/`items`, and the top-level `required` array.
 *
 * Accepts either:
 *  - A wrapped JSON Schema (`{ type: "object", properties: {...}, required: [...] }`):
 *    its `properties` are lifted to the top level and each property named in
 *    `required` is marked with `required: true` (others `required: false`) so the
 *    constraint survives in the flat shape the inference + MCP layers understand.
 *  - An already-flat map (`{ paramName: { type, ... } }`): returned verbatim.
 *
 * Returns `undefined` when the input carries no usable properties, so callers can
 * fall back to the schema auto-computed from `config.parameters`.
 */
export function normalizeAgentInputSchema(schema: unknown): FlatAgentInputSchema | undefined {
  if (!isPlainObject(schema)) {
    return undefined;
  }

  // Wrapped JSON Schema → lift `properties`, fold `required[]` per property.
  //
  // Detection requires a `properties` object PLUS a sibling wrapper signal
  // (`type: "object"`, a `required` array, or `additionalProperties`). A bare
  // `properties` object is otherwise ambiguous with a flat map that happens to
  // carry a parameter literally named "properties", so we leave that to the flat
  // branch rather than misreading the parameter as a wrapper.
  const hasRequired = Array.isArray(schema.required);
  const looksWrapped =
    isPlainObject(schema.properties) &&
    (schema.type === "object" || hasRequired || typeof schema.additionalProperties === "boolean");

  if (looksWrapped) {
    const properties = schema.properties as Record<string, unknown>;
    const requiredList = hasRequired ? (schema.required as unknown[]) : [];
    const requiredKeys = new Set(requiredList.filter((k): k is string => typeof k === "string"));

    const flat: FlatAgentInputSchema = {};
    for (const [key, value] of Object.entries(properties)) {
      if (!isPlainObject(value)) continue;
      flat[key] = { ...value };
      // Only project required when the caller actually declared a `required` array,
      // so we don't inject a misleading `required: false` onto every property.
      //
      // 🔴 AND ONLY THE `false` CASE IS WRITTEN, BECAUSE THE KEY COLLIDES. In the
      // flat shape `required` on a property is the platform's per-property
      // BOOLEAN flag; in JSON Schema the same key on an object property is that
      // object's own `required` ARRAY. Stamping `required: true` therefore
      // OVERWROTE the nested array of every required object property, silently
      // dropping its inner constraint. `false` is the only value that carries
      // information — `foldPropertyRequiredFlags` treats "no flag" as required,
      // which is the same verdict `true` produced — so omitting it is
      // behaviourally identical for every consumer AND leaves a nested array
      // intact on the properties that have one.
      //
      // The loss is halved rather than closed: an OPTIONAL object property still
      // has its nested array overwritten by the `false` flag, because the flat
      // shape has nowhere else to put it. That is the irreducible cost of a flat
      // representation and it is measured where it bites, at the MCP attach door.
      if (hasRequired && !requiredKeys.has(key)) {
        flat[key].required = false;
      }
    }
    return Object.keys(flat).length > 0 ? flat : undefined;
  }

  // Already a flat property-keyed map — keep only entries whose value is a property
  // definition object. This drops JSON Schema wrapper stubs (e.g. `{ type: "object" }`,
  // whose `type` value is a string, not a schema) so callers fall back to the
  // schema computed from `config.parameters` instead of persisting a broken map.
  const flat: FlatAgentInputSchema = {};
  for (const [key, value] of Object.entries(schema)) {
    if (!isPlainObject(value)) continue;
    flat[key] = value as Record<string, unknown>;
  }
  return Object.keys(flat).length > 0 ? flat : undefined;
}
